// src/pages/WHSAnalytics.tsx — G-06: WHS Performance Analytics Dashboard
// LTIFR · TRIFR · Lead & lag indicators · Monthly trend · AI forecast slot
// ISO 45001:2018 cl.9.1 · TMR WHS KPI reporting requirement

import { useState } from 'react'
import { useAuthStore } from '../stores/auth.store'
import { MOCK_WHS_MONTHLY, calcLTIFR, calcTRIFR, MOCK_CARS, MonthlyWHSStat } from '../utils/mockDataWHSMgmt'
import { MOCK_JOBS } from '../utils/mockData'
import { BarChart3, TrendingDown, TrendingUp, Minus, Download, Sparkles, AlertTriangle, CheckCircle, Clock, Shield } from 'lucide-react'

const fmtDate = (s: string) => new Date(s).toLocaleDateString('en-AU',{day:'numeric',month:'short',year:'2-digit'})

// ── Mini bar chart (CSS only — no recharts dependency) ──────────
function BarChart({ data, valueKey, label, color = '#2E75B6', maxOverride }: { data: MonthlyWHSStat[]; valueKey: keyof MonthlyWHSStat; label: string; color?: string; maxOverride?: number }) {
  const values = data.map(d => Number(d[valueKey]))
  const max    = maxOverride ?? Math.max(...values, 1)
  return (
    <div>
      <div className="text-xs font-medium text-gray-500 mb-2">{label}</div>
      <div className="flex items-end gap-1 h-20">
        {data.map((d, i) => {
          const v = Number(d[valueKey])
          const pct = max === 0 ? 0 : (v / max) * 100
          return (
            <div key={i} className="flex-1 flex flex-col items-center gap-0.5" title={`${d.label}: ${v}`}>
              <div className="text-[9px] text-gray-400">{v > 0 ? v : ''}</div>
              <div className="w-full rounded-t" style={{ height: `${Math.max(pct, v > 0 ? 8 : 0)}%`, background: color, minHeight: v > 0 ? '4px' : '0' }} />
              <div className="text-[9px] text-gray-400 rotate-0" style={{ fontSize: 8 }}>{d.month}</div>
            </div>
          )
        })}
      </div>
    </div>
  )
}

// ── Compliance rate bar ──────────────────────────────────────────
function RateBar({ completed, required, label }: { completed: number; required: number; label: string }) {
  const pct = required === 0 ? 100 : Math.round((completed / required) * 100)
  const color = pct === 100 ? 'bg-green-500' : pct >= 80 ? 'bg-amber-500' : 'bg-red-500'
  return (
    <div className="space-y-1">
      <div className="flex items-center justify-between text-xs">
        <span className="text-gray-600">{label}</span>
        <span className={`font-semibold ${pct === 100 ? 'text-green-700' : pct >= 80 ? 'text-amber-700' : 'text-red-700'}`}>{completed}/{required} ({pct}%)</span>
      </div>
      <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
        <div className={`h-full rounded-full ${color}`} style={{ width: `${pct}%` }} />
      </div>
    </div>
  )
}

// ── KPI card ────────────────────────────────────────────────────
function KPICard({ label, value, unit, sub, trend, color = 'text-gray-900', bg = 'bg-white' }: { label: string; value: string | number; unit?: string; sub?: string; trend?: 'up' | 'down' | 'flat'; color?: string; bg?: string }) {
  const TrendIcon = trend === 'up' ? TrendingUp : trend === 'down' ? TrendingDown : Minus
  const trendColor = trend === 'up' ? 'text-red-500' : trend === 'down' ? 'text-green-500' : 'text-gray-400'
  return (
    <div className={`${bg} border border-gray-200 rounded-xl px-4 py-3`}>
      <div className="text-xs font-medium text-gray-500 mb-1">{label}</div>
      <div className="flex items-end gap-1">
        <span className={`text-2xl font-bold ${color}`}>{value}</span>
        {unit && <span className="text-xs text-gray-400 mb-1">{unit}</span>}
      </div>
      {(sub || trend) && (
        <div className="flex items-center gap-1 mt-0.5">
          {trend && <TrendIcon className={`w-3 h-3 ${trendColor}`} />}
          {sub && <span className="text-xs text-gray-400">{sub}</span>}
        </div>
      )}
    </div>
  )
}

export function WHSAnalytics() {
  const { isTier } = useAuthStore()
  const canExport = isTier('B_PM') || isTier('A_DEVELOPER')
  const [jobFilter, setJobFilter] = useState('ALL')

  const stats = MOCK_WHS_MONTHLY
  const ltifr  = calcLTIFR(stats)
  const trifr  = calcTRIFR(stats)
  const totalHours   = stats.reduce((a,s) => a + s.hoursWorked, 0)
  const totalInc     = stats.reduce((a,s) => a + s.incidents, 0)
  const totalLTI     = stats.reduce((a,s) => a + s.lti, 0)
  const totalNearMiss= stats.reduce((a,s) => a + s.nearMisses, 0)
  const totalInspComp= stats.reduce((a,s) => a + s.inspectionsCompleted, 0)
  const totalInspReq = stats.reduce((a,s) => a + s.inspectionsRequired, 0)
  const totalTbkComp = stats.reduce((a,s) => a + s.toolboxCompleted, 0)
  const totalTbkReq  = stats.reduce((a,s) => a + s.toolboxRequired, 0)
  const openCARs  = MOCK_CARS.filter(c => ['OPEN','IN_PROGRESS','OVERDUE'].includes(c.status)).length
  const overdueCARs = MOCK_CARS.filter(c => c.status === 'OVERDUE').length

  // Industry benchmark for electrical/civil construction
  const LTIFR_BENCHMARK = 4.8
  const TRIFR_BENCHMARK = 14.2

  return (
    <div className="space-y-5">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2"><BarChart3 className="w-6 h-6 text-[#2E75B6]"/>WHS Performance Analytics</h1>
          <p className="text-sm text-gray-500 mt-0.5">LTIFR · TRIFR · lead & lag indicators · {totalHours.toLocaleString()} hours YTD · ISO 45001:2018 cl.9.1</p>
        </div>
        <div className="flex gap-2">
          <select className="input text-sm w-auto" value={jobFilter} onChange={e => setJobFilter(e.target.value)}>
            <option value="ALL">All job sites</option>
            {MOCK_JOBS.filter(j => !['COMPLETED','CLOSED'].includes(j.status)).map(j => <option key={j.id} value={j.id}>{j.jobNumber}</option>)}
          </select>
          {canExport && <button className="btn-secondary text-sm flex items-center gap-2"><Download className="w-4 h-4"/>Export WHS report</button>}
        </div>
      </div>

      {/* ── KPI strip ── */}
      <div className="grid grid-cols-5 gap-3">
        <KPICard label="LTIFR" value={ltifr} unit="per M hrs" sub={`Benchmark: ${LTIFR_BENCHMARK}`} trend={ltifr > LTIFR_BENCHMARK ? 'up' : ltifr === 0 ? 'flat' : 'down'} color={ltifr > LTIFR_BENCHMARK ? 'text-red-600' : 'text-green-700'} bg={ltifr > LTIFR_BENCHMARK ? 'bg-red-50' : 'bg-green-50'} />
        <KPICard label="TRIFR" value={trifr} unit="per M hrs" sub={`Benchmark: ${TRIFR_BENCHMARK}`} trend={trifr > TRIFR_BENCHMARK ? 'up' : 'down'} color={trifr > TRIFR_BENCHMARK ? 'text-amber-700' : 'text-green-700'} />
        <KPICard label="Near miss rate" value={totalNearMiss} unit="events YTD" sub="Target: report all" trend="flat" />
        <KPICard label="Inspection compliance" value={`${Math.round(totalInspComp/totalInspReq*100)}%`} sub={`${totalInspComp}/${totalInspReq} completed`} trend={totalInspComp >= totalInspReq ? 'flat' : 'up'} color={totalInspComp >= totalInspReq ? 'text-green-700' : 'text-amber-700'} />
        <KPICard label="Toolbox compliance" value={`${Math.round(totalTbkComp/totalTbkReq*100)}%`} sub={`${totalTbkComp}/${totalTbkReq} completed`} trend={totalTbkComp >= totalTbkReq ? 'flat' : 'up'} color={totalTbkComp >= totalTbkReq ? 'text-green-700' : 'text-amber-700'} />
      </div>

      <div className="grid grid-cols-3 gap-4">
        {/* ── Lag indicators ── */}
        <div className="col-span-2 space-y-4">
          <div className="bg-white border border-gray-200 rounded-xl p-5">
            <div className="text-sm font-semibold text-gray-900 mb-4">Lag indicators — incident trend (YTD 2026)</div>
            <div className="grid grid-cols-3 gap-4 mb-5">
              <div className="text-center"><div className="text-3xl font-bold text-red-600">{totalInc}</div><div className="text-xs text-gray-500 mt-0.5">Total incidents</div></div>
              <div className="text-center"><div className="text-3xl font-bold text-orange-600">{totalLTI}</div><div className="text-xs text-gray-500 mt-0.5">Lost time injuries</div></div>
              <div className="text-center"><div className="text-3xl font-bold text-amber-600">{totalNearMiss}</div><div className="text-xs text-gray-500 mt-0.5">Near misses</div></div>
            </div>
            <BarChart data={stats} valueKey="incidents" label="Total incidents per month" color="#EF4444" />
          </div>

          <div className="bg-white border border-gray-200 rounded-xl p-5">
            <div className="text-sm font-semibold text-gray-900 mb-4">Lead indicators — compliance performance</div>
            <div className="space-y-3 mb-5">
              <RateBar completed={totalInspComp} required={totalInspReq} label="WHS inspections (YTD)" />
              <RateBar completed={totalTbkComp}  required={totalTbkReq}  label="Toolbox talks (YTD)" />
              <RateBar completed={MOCK_CARS.filter(c=>c.status==='VERIFIED').length} required={MOCK_CARS.length} label="Corrective actions closed / verified" />
            </div>
            <BarChart data={stats} valueKey="inspectionsCompleted" label="Inspections completed per month" color="#2E75B6" maxOverride={12} />
          </div>
        </div>

        {/* ── Right panel ── */}
        <div className="space-y-4">
          {/* LTIFR vs benchmark */}
          <div className="bg-white border border-gray-200 rounded-xl p-5">
            <div className="text-sm font-semibold text-gray-900 mb-3">LTIFR vs industry benchmark</div>
            <div className="space-y-3">
              <div>
                <div className="flex justify-between text-xs mb-1"><span className="text-gray-600">MCB Civil Electrical</span><span className={`font-bold ${ltifr > LTIFR_BENCHMARK ? 'text-red-600' : 'text-green-700'}`}>{ltifr}</span></div>
                <div className="h-3 bg-gray-100 rounded-full overflow-hidden"><div className="h-full rounded-full bg-[#2E75B6]" style={{ width: `${Math.min((ltifr / 10) * 100, 100)}%` }} /></div>
              </div>
              <div>
                <div className="flex justify-between text-xs mb-1"><span className="text-gray-500">Elec/Civil benchmark</span><span className="font-medium text-gray-600">{LTIFR_BENCHMARK}</span></div>
                <div className="h-3 bg-gray-100 rounded-full overflow-hidden"><div className="h-full rounded-full bg-gray-300" style={{ width: `${Math.min((LTIFR_BENCHMARK / 10) * 100, 100)}%` }} /></div>
              </div>
            </div>
            <div className={`mt-3 text-xs px-2 py-1.5 rounded-lg font-medium ${ltifr <= LTIFR_BENCHMARK ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'}`}>
              {ltifr === 0 ? '✓ No lost time injuries YTD' : ltifr <= LTIFR_BENCHMARK ? `✓ ${((1 - ltifr/LTIFR_BENCHMARK)*100).toFixed(0)}% below industry benchmark` : `⚠ ${((ltifr/LTIFR_BENCHMARK - 1)*100).toFixed(0)}% above industry benchmark`}
            </div>
          </div>

          {/* CAR summary */}
          <div className={`border rounded-xl p-5 ${overdueCARs > 0 ? 'bg-red-50 border-red-200' : 'bg-white border-gray-200'}`}>
            <div className="text-sm font-semibold text-gray-900 mb-3 flex items-center gap-2">
              <AlertTriangle className={`w-4 h-4 ${overdueCARs > 0 ? 'text-red-600' : 'text-gray-400'}`}/>Corrective actions
            </div>
            {[{ label:'Open', value: MOCK_CARS.filter(c=>c.status==='OPEN').length, color:'text-amber-700' },{ label:'In progress', value: MOCK_CARS.filter(c=>c.status==='IN_PROGRESS').length, color:'text-blue-700' },{ label:'Overdue', value: overdueCARs, color:'text-red-700' },{ label:'Closed / verified', value: MOCK_CARS.filter(c=>['CLOSED','VERIFIED'].includes(c.status)).length, color:'text-green-700' }].map(item => (
              <div key={item.label} className="flex justify-between text-sm py-1 border-b border-gray-100 last:border-0">
                <span className="text-gray-600">{item.label}</span>
                <span className={`font-semibold ${item.color}`}>{item.value}</span>
              </div>
            ))}
          </div>

          {/* AI forecast slot */}
          <div className="bg-gradient-to-br from-blue-50 to-purple-50 border border-blue-200 rounded-xl p-4">
            <div className="flex items-center gap-2 mb-2"><Sparkles className="w-4 h-4 text-[#2E75B6]"/><div className="text-xs font-semibold text-blue-900">AI Compliance Forecast</div></div>
            <div className="text-xs text-blue-700 space-y-1.5">
              <div className="flex items-start gap-1.5"><span className="text-amber-500 flex-shrink-0">⚠</span>CAR-2026-001 due date in <strong>2 days</strong> — no evidence uploaded yet</div>
              <div className="flex items-start gap-1.5"><span className="text-amber-500 flex-shrink-0">⚠</span>HRCW notification for TP-2026-003 approaching — contract value exceeds $250k</div>
              <div className="flex items-start gap-1.5"><span className="text-blue-500 flex-shrink-0">ℹ</span>Inspection compliance trending down — 3 inspections outstanding this month</div>
            </div>
            <div className="mt-2 text-[10px] text-blue-500 italic">Predictive alerts — WHS AI Agent (G-18 · AI Phase)</div>
          </div>
        </div>
      </div>

      {/* Hours trend */}
      <div className="bg-white border border-gray-200 rounded-xl p-5">
        <div className="text-sm font-semibold text-gray-900 mb-4">Hours worked — all sites (YTD 2026)</div>
        <BarChart data={stats} valueKey="hoursWorked" label="Hours worked per month" color="#22C55E" maxOverride={3000} />
        <div className="text-xs text-gray-400 mt-2">Total hours YTD: <strong className="text-gray-700">{totalHours.toLocaleString()} hrs</strong> · Used as denominator for LTIFR and TRIFR calculations</div>
      </div>
    </div>
  )
}
